/**
 * RWBR — FEUDING AND FUMING drivers. Rulebook p.x + the FEUD/FUME RESULTS table on Board A.
 *
 * p.x — "On a metallic dice roll of '7' or if the metallic dice are doubles on a
 * DUEL, FEUDING and FUMING drivers go off the deep end, frequently causing
 * mayhem. To determine a FEUD/FUME result, check the FEUD/FUME list on your
 * score sheet. If there's more than one FEUD or FUMING driver, then roll a die
 * to determine which driver is the hot-head this time. (If there are no drivers
 * on the list, then nothing happens — simply proceed to the next turn.)"
 *
 * RABBIT AND TURTLE
 *   "The driver in the FEUD who's ahead is considered to be the 'rabbit,' and
 *    the trailing driver is considered to be the 'turtle.'"
 *   The same pairing is used for a FUMING driver and whoever he vents on.
 *   Note the table is deliberately lopsided — the turtle suffers on 2, 8, 9, 10,
 *   11 and 12, the rabbit on 3, 4, 5, 7, 11 and 12 — so getting this backwards
 *   would quietly bias every feud in the game.
 *
 * WHAT THIS MODULE DOES NOT DO
 *   "TROUBLE" rows hand a whole group to the trouble module; "OUT" and "to
 *   bottom" rows report the card movement but do not run FILLING A GAP (p.vi),
 *   which happens once, at the end of the turn, with its own dice.
 */

import type { Dice } from './dice.ts';
import type { Car, Group, RaceState } from './types.ts';
import { groupOf } from './types.ts';
import type { DiceRecord, PendingTrouble, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { fieldFromState, membersOf, nameOf, positionOf } from './ruleEvent.ts';

// ---------------------------------------------------------------------------
// Board A — the FEUD/FUME RESULTS table
// ---------------------------------------------------------------------------

export type Duelist = 'rabbit' | 'turtle';

/** Ledger labels for the rolls a FEUD/FUME can make. */
export const HOT_HEAD_ROLL = 'hot-head die';
export const FUME_TARGET_ROLL = 'fume target die';
export const FEUD_FUME_ROLL = 'FEUD/FUME RESULTS metallic total';

export interface FeudFumeChartEntry {
  /** Printed exactly as it appears on Board A. */
  text: string;
  /** Wrecked out of the race. */
  out: Duelist[];
  /** Card to the end of the BOTTOM group stack. */
  toBottom: Duelist[];
  /** "treat this as a TROUBLE Race Cam result for all drivers in the indicated group" (p.x). */
  trouble: boolean;
  yellow: boolean;
}

/**
 * Read as a SUM of the two metallic dice (2-12).
 * Source: Board A (bd_01.jpg) and the "Feud Fume" sheet, which agree word for
 * word — including the two rows printed without a terminating period.
 */
export const FEUD_FUME_RESULTS: Readonly<Record<number, FeudFumeChartEntry>> = {
  2: { text: 'Turtle OUT + TROUBLE.', out: ['turtle'], toBottom: [], trouble: true, yellow: false },
  3: { text: 'Rabbit OUT + TROUBLE.', out: ['rabbit'], toBottom: [], trouble: true, yellow: false },
  4: { text: 'Rabbit to bottom + TROUBLE', out: [], toBottom: ['rabbit'], trouble: true, yellow: false },
  5: { text: 'Rabbit OUT + YELLOW.', out: ['rabbit'], toBottom: [], trouble: false, yellow: true },
  6: { text: 'Turtle and Rabbit to bottom.', out: [], toBottom: ['rabbit', 'turtle'], trouble: false, yellow: false },
  7: { text: 'Rabbit to bottom.', out: [], toBottom: ['rabbit'], trouble: false, yellow: false },
  8: { text: 'Turtle to bottom.', out: [], toBottom: ['turtle'], trouble: false, yellow: false },
  9: { text: 'Turtle OUT + YELLOW', out: ['turtle'], toBottom: [], trouble: false, yellow: true },
  10: { text: 'Turtle to bottom + YELLOW.', out: [], toBottom: ['turtle'], trouble: false, yellow: true },
  11: { text: 'Turtle and Rabbit OUT + YELLOW.', out: ['rabbit', 'turtle'], toBottom: [], trouble: false, yellow: true },
  12: { text: 'Turtle and Rabbit OUT + TROUBLE.', out: ['rabbit', 'turtle'], toBottom: [], trouble: true, yellow: false },
};

// ---------------------------------------------------------------------------
// Picking the hot-head
// ---------------------------------------------------------------------------

export type FeudFumeKind = 'feud' | 'fume';

export interface FeudFumeCandidate {
  kind: FeudFumeKind;
  /** For a feud, both parties. For a fume, the fuming driver only. */
  driverIds: string[];
}

export interface FeudFumePairing {
  kind: FeudFumeKind;
  /** The driver who is AHEAD (p.x). */
  rabbitId: string;
  /** The driver who is BEHIND (p.x). */
  turtleId: string;
  rabbitPosition: number;
  turtlePosition: number;
}

export interface FeudFumeSelectOptions {
  /**
   * Who a fuming driver vents on. p.x — "that driver may choose to 'vent' his
   * anger on ANY driver in his group. You can make that choice yourself, or roll
   * a die to determine the target driver randomly." Omit to roll.
   */
  fumeTargetId?: string;
  /** Force a particular candidate instead of rolling for the hot-head. Index into `candidates`. */
  candidateIndex?: number;
  /**
   * Roll the chart for THESE TWO, skipping candidate selection entirely.
   *
   * The POST RACE INTERVIEW's FIRST DUEL result — *"One first Duel roll,
   * immediately roll on Feud/Fume Chart for Racers involved"* — names the two
   * racers itself, and neither of them need be on the feud or the fume list.
   * Selection is what the lists are FOR, so a caller that already knows the two
   * names must be able to skip it rather than fake a list entry to get in.
   *
   * `kind` is `'feud'` because the pair is named and mutual, which is what the
   * feud branch of `pairing` means. It is NOT an assertion that a feud exists —
   * the chart roll is what decides whether one starts.
   */
  forcedPair?: { a: string; b: string };
}

/**
 * Everyone eligible to blow up this turn.
 *
 * AMBIGUITY: the rulebook never says what happens when a listed feud involves a
 * driver who is already out of the race, or when a fuming driver has his group
 * to himself. Both are treated as ineligible rather than as a no-op turn, so a
 * stale list entry cannot swallow the result.
 */
export function feudFumeCandidates(state: RaceState): FeudFumeCandidate[] {
  const order = fieldFromState(state);
  const running = new Set(order.map((c) => c.card.driverId));
  const candidates: FeudFumeCandidate[] = [];

  for (const feud of state.feuds) {
    if (running.has(feud.a) && running.has(feud.b)) {
      candidates.push({ kind: 'feud', driverIds: [feud.a, feud.b] });
    }
  }
  for (const driverId of state.fuming) {
    if (!running.has(driverId)) continue;
    const group = groupOf(positionOf(order, driverId));
    const others = membersOf(order, group).filter((c) => c.card.driverId !== driverId);
    if (others.length > 0) candidates.push({ kind: 'fume', driverIds: [driverId] });
  }
  return candidates;
}

/** p.x — whoever is ahead is the rabbit, whoever trails is the turtle. */
export function pairing(order: readonly Car[], kind: FeudFumeKind, a: string, b: string): FeudFumePairing {
  const posA = positionOf(order, a);
  const posB = positionOf(order, b);
  const aAhead = posA < posB;
  return {
    kind,
    rabbitId: aAhead ? a : b,
    turtleId: aAhead ? b : a,
    rabbitPosition: Math.min(posA, posB),
    turtlePosition: Math.max(posA, posB),
  };
}

// ---------------------------------------------------------------------------
// Resolving
// ---------------------------------------------------------------------------

export type FeudFumeMoment =
  | 'feud.none'
  | 'feud.wreck'
  | 'feud.demote'
  | 'feud.trouble'
  | 'fume.wreck'
  | 'fume.demote'
  | 'fume.trouble';

export interface FeudFumeResult extends RuleEvent {
  eventType: 'feud-fume';
  momentTag: FeudFumeMoment;
  kind: FeudFumeKind | null;
  pairing: FeudFumePairing | null;
  /** Everyone who was eligible when the dice came out. */
  candidates: FeudFumeCandidate[];
  /** 2-12 on the metallic dice, null when the list was empty. */
  total: number | null;
  entry: FeudFumeChartEntry | null;
  /** Wrecked out of the race. */
  out: string[];
  /** Cards to the end of the BOTTOM stack. */
  toBottom: string[];
  yellowFlag: boolean;
  /** Yellow-flag turns to burn. One or two cars collected = one turn (p.v). */
  yellowTurns: number;
  /** Groups handed to the trouble module as a full TROUBLE Race Cam result. */
  pendingTrouble: PendingTrouble[];
  /** Slots left open in the LEADER/TOP/MIDDLE boards; FILL A GAP is the caller's job (p.vi). */
  vacatedPositions: number[];
  gapFillRequired: boolean;
}

/**
 * Resolve a FEUD/FUME. Rolls for the hot-head if the list holds more than one,
 * then rolls the metallic dice on Board A's FEUD/FUME RESULTS table.
 */
export function resolveFeudFume(
  state: RaceState,
  dice: Dice,
  options: FeudFumeSelectOptions = {},
): FeudFumeResult {
  const order = fieldFromState(state);
  const candidates = feudFumeCandidates(state);

  // A forced pair brings its own two names, so the "nobody is on the list"
  // short-circuit below must not fire on it — the interview result is the
  // reason this roll is happening and the lists have nothing to do with it.
  const forced = options.forcedPair ?? null;

  // p.x — "If there are no drivers on the list, then nothing happens."
  if (!forced && candidates.length === 0) {
    return {
      eventType: 'feud-fume',
      momentTag: 'feud.none',
      ruleCitation: 'RB/x',
      actorIds: [],
      positionDeltas: [],
      modifiers: [],
      dice: [],
      description: 'Tempers flare somewhere in the garage, but nobody on the feud or fume list is in a position to do anything about it. Nothing happens.',
      kind: null,
      pairing: null,
      candidates,
      total: null,
      entry: null,
      out: [],
      toBottom: [],
      yellowFlag: false,
      yellowTurns: 0,
      pendingTrouble: [],
      vacatedPositions: [],
      gapFillRequired: false,
    };
  }

  // p.x — "If there's more than one FEUD or FUMING driver, then roll a die to
  // determine which driver is the hot-head this time."
  // AMBIGUITY: a physical d6 cannot pick fairly from more than six entries, and
  // the rulebook does not say what to do with a longer list. A die of exactly
  // the right number of faces is used instead, which reduces to the printed
  // procedure whenever the list is six or shorter.
  let hotHeadDie: number | null = null;
  let index = options.candidateIndex ?? 0;
  if (!forced && options.candidateIndex === undefined && candidates.length > 1) {
    hotHeadDie = dice.about(candidates.map((c) => c.driverIds[0]), () => dice.select(candidates.length), 'hot-head');
    index = hotHeadDie - 1;
  }
  const candidate: FeudFumeCandidate = forced
    ? { kind: 'feud', driverIds: [forced.a, forced.b] }
    : candidates[index];
  if (!candidate) throw new RangeError(`no feud/fume candidate at index ${index}`);

  // p.x — a FEUD drags in the other party; a FUMING driver picks anyone in his group.
  let fumeTargetDie: number | null = null;
  let partnerId: string;
  if (candidate.kind === 'feud') {
    partnerId = candidate.driverIds[1];
  } else {
    const group = groupOf(positionOf(order, candidate.driverIds[0]));
    const pool = membersOf(order, group).filter((c) => c.card.driverId !== candidate.driverIds[0]);
    if (options.fumeTargetId) {
      const chosen = pool.find((c) => c.card.driverId === options.fumeTargetId);
      if (!chosen) {
        throw new Error(
          `${options.fumeTargetId} is not in the fuming driver's own group — p.x limits venting to "ANY driver in his group".`,
        );
      }
      partnerId = chosen.card.driverId;
    } else {
      fumeTargetDie = dice.about(pool.map((c) => c.card.driverId), () => dice.select(pool.length), 'fume-target');
      partnerId = pool[fumeTargetDie - 1].card.driverId;
    }
  }

  const pair = pairing(order, candidate.kind, candidate.driverIds[0], partnerId);

  const roll = dice.sum2d6();
  const diceRecords: DiceRecord[] = [];
  if (hotHeadDie !== null) {
    diceRecords.push({ kind: 'd6', dice: [hotHeadDie], label: HOT_HEAD_ROLL, faces: { hotHead: hotHeadDie } });
  }
  if (fumeTargetDie !== null) {
    diceRecords.push({ kind: 'd6', dice: [fumeTargetDie], label: FUME_TARGET_ROLL, faces: { fumeTarget: fumeTargetDie } });
  }
  diceRecords.push({
    kind: '2d6-sum',
    dice: roll.dice,
    total: roll.total,
    label: FEUD_FUME_ROLL,
    flags: { doubles: roll.doubles },
  });
  const entry = FEUD_FUME_RESULTS[roll.total];
  if (!entry) throw new RangeError(`metallic total ${roll.total} is not on the FEUD/FUME table`);

  const idOf = (d: Duelist) => (d === 'rabbit' ? pair.rabbitId : pair.turtleId);
  const posOf = (d: Duelist) => (d === 'rabbit' ? pair.rabbitPosition : pair.turtlePosition);

  const out = entry.out.map(idOf);
  const toBottom = entry.toBottom.map(idOf);

  // Work out where the demoted cards land. Cars that are OUT leave the field
  // entirely, so the stack is one shorter for anyone sent to the back.
  const fieldAfter = order.length - out.length;
  const positionDeltas: PositionDelta[] = [];
  for (const d of entry.out) {
    positionDeltas.push({ driverId: idOf(d), from: posOf(d), to: null });
  }
  // AMBIGUITY: when both cards go to the bottom on a 6, the rulebook does not
  // say which lands last. Their running order is preserved — the rabbit was
  // ahead and stays ahead.
  const demotedInOrder = [...entry.toBottom].sort((x, y) => posOf(x) - posOf(y));
  demotedInOrder.forEach((d, i) => {
    positionDeltas.push({
      driverId: idOf(d),
      from: posOf(d),
      to: fieldAfter - demotedInOrder.length + 1 + i,
    });
  });

  // "TROUBLE means the feud has caused trouble on the track, and you'll treat
  // this as a TROUBLE Race Cam result for all drivers in the indicated group."
  // AMBIGUITY: "the indicated group" is not defined for a feud whose two parties
  // sit in different groups — the p.x example has exactly that. Read as the
  // group(s) of the driver(s) the result names, deduplicated.
  const troubleGroups: Group[] = [];
  if (entry.trouble) {
    for (const d of [...entry.out, ...entry.toBottom]) {
      const g = groupOf(posOf(d));
      if (!troubleGroups.includes(g)) troubleGroups.push(g);
    }
  }
  const pendingTrouble: PendingTrouble[] = troubleGroups.map((group) => ({
    scope: 'group',
    group,
    source: `${candidate.kind}-result-${roll.total}`,
    ruleCitation: 'RB/x + Board A',
  }));

  // p.v — one or two cars collected brings out one yellow-flag turn. No row on
  // this table can involve more than two cars.
  const yellowTurns = entry.yellow ? 1 : 0;

  const vacatedPositions = [...entry.out, ...entry.toBottom]
    .map(posOf)
    .filter((p) => groupOf(p) !== 'bottom')
    .sort((a, b) => a - b);

  const momentTag: FeudFumeMoment =
    entry.out.length > 0
      ? (`${candidate.kind}.wreck` as FeudFumeMoment)
      : entry.toBottom.length > 0
        ? (`${candidate.kind}.demote` as FeudFumeMoment)
        : (`${candidate.kind}.trouble` as FeudFumeMoment);

  const rabbit = order[pair.rabbitPosition - 1];
  const turtle = order[pair.turtlePosition - 1];
  const label = (d: Duelist) => nameOf(d === 'rabbit' ? rabbit : turtle);

  const consequences: string[] = [];
  for (const d of entry.out) consequences.push(`${label(d)} is wrecked out of the race`);
  for (const d of entry.toBottom) consequences.push(`${label(d)} is shuffled to the back of the field`);
  if (entry.trouble) consequences.push(`and it collects the ${troubleGroups.join(' and ')} group on the track`);
  if (entry.yellow) consequences.push('and the yellow comes out');

  const opener =
    candidate.kind === 'feud'
      ? `The ${nameOf(rabbit)}–${nameOf(turtle)} feud boils over`
      : `${nameOf(order[positionOf(order, candidate.driverIds[0]) - 1])} has had enough and vents on ` +
        `${nameOf(candidate.driverIds[0] === pair.rabbitId ? turtle : rabbit)}`;

  return {
    eventType: 'feud-fume',
    momentTag,
    ruleCitation: 'RB/x + Board A',
    actorIds: [pair.rabbitId, pair.turtleId],
    positionDeltas,
    modifiers: [
      {
        name: 'rabbit-is-the-car-ahead',
        value: 0,
        note: `${nameOf(rabbit)} is the rabbit at P${pair.rabbitPosition}; ${nameOf(turtle)} is the turtle at P${pair.turtlePosition} (p.x)`,
      },
    ],
    dice: diceRecords,
    description: `${opener}. Metallic ${roll.total}: "${entry.text}" — ${consequences.join(', ')}.`,
    kind: candidate.kind,
    pairing: pair,
    candidates,
    total: roll.total,
    entry,
    out,
    toBottom,
    yellowFlag: entry.yellow,
    yellowTurns,
    pendingTrouble,
    vacatedPositions,
    gapFillRequired: vacatedPositions.length > 0,
  };
}
